import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaFlask, FaHandshake, FaLeaf, FaSeedling, FaRecycle, FaChevronDown, FaChevronRight, FaCanadianMapleLeaf, FaLightbulb, FaHandHolding, FaAward, FaEye, FaBullseye, FaStar, FaGem, FaRocket, FaShieldAlt } from 'react-icons/fa';
import './css/AboutUs.css';
import aboutUsImg from '../assets/images/About us img.png';
import SEO from '../components/SEO';
import { organizationSchema } from '../components/StructuredData';

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: 60 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

const fadeInRight = {
  hidden: { opacity: 0, x: -60 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

const SectionHeader = ({ title, subtitle }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <div className="section-header text-center" ref={ref}>
      <motion.div
        variants={{
          hidden: { opacity: 0, y: 20 },
          visible: { opacity: 1, y: 0 }
        }}
        initial="hidden"
        animate={controls}
        transition={{ duration: 0.7 }}
      >
        <h2 className="section-title">{title}</h2>
        {subtitle && <p className="section-subtitle">{subtitle}</p>}
      </motion.div>
    </div>
  );
};

const CoreValue = ({ title, description, icon: Icon, index }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <motion.div 
      className="core-value-item"
      ref={ref}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      initial="hidden"
      animate={controls}
      transition={{ duration: 0.7, delay: index * 0.1 }}
      whileHover={{ 
        scale: 1.05,
        boxShadow: "0 20px 30px -10px rgba(0, 0, 0, 0.15)",
        backgroundColor: "rgba(255, 255, 255, 0.95)"
      }}
    >
      <div className="core-value-icon-wrapper">
        <div className="core-value-icon">
          <Icon size={40} />
        </div>
      </div>
      <h3 className="core-value-title text-center">{title}</h3>
      <p className="core-value-description">{description}</p>
    </motion.div>
  );
};

const AboutUs = () => {
  return (
    <>
      <SEO
        title="About Nimble Group - 25+ Years of Eco-Friendly Chemical Innovation"
        description="Learn about Nimble Group's journey in sustainable chemical solutions. With 25+ years of expertise, we're pioneers in green chemistry, serving industries with eco-friendly alternatives and innovative solutions."
        keywords="about nimble group, chemical company history, sustainable solutions, green chemistry pioneers, eco-friendly manufacturing, environmental responsibility, chemical innovation"
        canonical="/about"
        structuredData={organizationSchema}
      />
      <AboutUsContent />
    </>
  );
};

const AboutUsContent = () => {
  const coreValues = [
    {
      title: "Innovation with Purpose",
      description: "We believe meaningful innovation is the pathway to progress. By challenging the ordinary, we create distinctive, impactful solutions that elevate industries and safeguard the environment.",
      icon: FaRocket
    },
    {
      title: "Integrity & Transparency",
      description: "Honesty and fairness guide our relationships with employees, customers, suppliers, and stakeholders. We uphold the highest standards of ethical conduct and open communication.",
      icon: FaShieldAlt
    },
    {
      title: "Commitment to EHS",
      description: "Every product, process, and partnership reflects our uncompromising dedication to Environment, Health, and Safety for our teams, our customers, and the communities we touch.",
      icon: FaLeaf
    },
    {
      title: "Growing by Giving",
      description: "We believe that true growth happens by giving to our customers through value-driven solutions, to our employees through trust and opportunities, to our society through mindful actions, and to Mother Nature by minimizing our ecological footprint.",
      icon: FaGem
    }
  ];

  const legacyItems = [
    "Aromatic Chemicals & Derivatives",
    "Eco-Friendly & Sustainable Industrial Cleaners",
    "Advanced Heat Dissipation Compounds",
    "Green Metal Working Additives"
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <div className="about-hero-section">
        <div className="about-hero-overlay"></div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="about-hero-content"
          >
            <h1 className="about-hero-title">ABOUT US</h1>
            <div className="about-hero-underline"></div>
            <p className="about-hero-subtitle">Shaping a Sustainable Industrial Future</p>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="about-scroll-indicator"
            >
              <FaChevronDown />
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      {/* Introduction Section */}
      <section className="about-intro-section">
        <div className="container">
          <div className="about-intro-grid">
            <motion.div 
              className="about-intro-content"
              variants={fadeInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <h2 className="about-intro-title">Our <span className="text-accent">Story</span></h2>
              <div className="about-intro-divider"></div>
              <p className="content-justified">We are a team of technocrats committed to shaping a more responsible industrial future through the power of science, engineering, and green chemistry. Since our inception in 1996, we have continuously designed eco-friendly, sustainable chemical solutions that help industries elevate their Environment, Health, and Safety (EHS) standards while advancing operational efficiency.</p>
              <p className="content-justified">At our core lies a belief: business growth must serve a higher purpose — improving the well-being of our customers, employees, society, and Mother Earth. Every product we innovate, every solution we customize, and every relationship we nurture is built on the principle of creating value not just for business, but for life itself.</p>
            </motion.div>
            
          <motion.div 
              className="about-intro-image"
              variants={fadeInLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
              <img src={aboutUsImg} alt="About Nimble Technologies" />
              <div className="image-decoration-dot dot-1"></div>
              <div className="image-decoration-dot dot-2"></div>
              <div className="image-decoration-line"></div>
          </motion.div>
          </div>
          
          <motion.div 
            className="about-highlights"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            {[
              { number: "30+", text: "Years of Excellence", icon: FaAward },
              { number: "100+", text: "Global Clients", icon: FaHandshake },
              { number: "4", text: "Business Verticals", icon: FaLightbulb },
              { number: "50+", text: "Green Products", icon: FaCanadianMapleLeaf }
            ].map((highlight, index) => (
              <motion.div 
                key={highlight.text}
                className="highlight-item"
                whileHover={{ scale: 1.08, boxShadow: "0 8px 32px rgba(22, 163, 74, 0.12)" }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="highlight-icon">
                  {React.createElement(highlight.icon, { size: 32 })}
                </div>
                <div className="highlight-number">{highlight.number}</div>
                <div className="highlight-text">{highlight.text}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="vision-mission-section">
        <div className="vision-mission-background-elements">
          <div className="vision-circle circle-1"></div>
          <div className="vision-circle circle-2"></div>
          <div className="mission-shape shape-1"></div>
          <div className="mission-shape shape-2"></div>
        </div>
        <div className="container-about">
          <SectionHeader 
            title="Our Vision & Mission" 
          />
          <div className="vision-mission-grid">
            <motion.div 
              className="vision-content content-justified"
              variants={fadeInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="vision-header">
                <div className="vision-icon-wrapper">
                  <FaEye className="vision-icon" />
                </div>
                <h3 className="vision-mission-title">
                  Our Vision
                </h3>
              </div>
              <div className="vision-container">
                <div className="vision-quote-accent">
                  <FaStar className="accent-star" />
                </div>
                <p className="vision-text">To become the most preferred global partner, admired for advancing eco-friendly, sustainable solutions that empower industries to enhance their EHS performance and contribute to a greener, more responsible world. We aspire to be remembered as a company that gave more than it took, enriching ecosystems and inspiring responsible industrial practices.</p>
              </div>
            </motion.div>

            <motion.div 
              className="mission-content content-justified"
              variants={fadeInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="mission-header">
                <div className="mission-icon-wrapper">
                  <FaBullseye className="mission-icon" />
                </div>
                <h3 className="vision-mission-title">
                  Our Mission
                </h3>
              </div>
              <motion.div 
                className="mission-box"
              >
                <div className="mission-quote-accent">
                  <FaRocket className="accent-rocket" />
                </div>
                <p className="mission-text">We are committed to improving the Environment, Health, and Safety standards of industries by designing sustainable, eco-conscious solutions based on green chemistry and renewable resources. We strive to be a catalyst for positive change, helping our customers transition towards green manufacturing ecosystems and nurturing a business culture rooted in care, responsibility, and innovation.</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="core-values-section">
        <div className="container-values">
          <SectionHeader 
            title="Our Core Values" 
          />
          <motion.div 
            className="core-values-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {coreValues.map((value, index) => (
              <CoreValue 
                key={value.title} 
                {...value} 
                index={index}
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Sustainable Chemistry Section */}
      <section className="sustainable-chemistry-section">
        <div className="container">
          <SectionHeader title="Our Sustainable Chemistry Philosophy" />
          <motion.div 
            className="sustainable-content content-justified"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div 
              className="sustainable-box"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <div className="sustainable-icon">
                <FaRecycle size={50} />
              </div>
              <p>We firmly believe that science should serve life. Our philosophy centers around creating green chemistry-based solutions that protect both people and the planet. By harnessing bio-renewable resources and continuously reducing dependency on hazardous substances, we help industries achieve operational excellence without compromising environmental responsibility.</p>
              <p>For us, sustainability is not a business strategy — it's a way of life. We honor not just our customers and communities, but also the unseen stakeholders in every process: the air we breathe, the water we use, the machines we build, and the ecosystems we inhabit.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Legacy Section */}
      <section className="legacy-section">
        <div className="container">
          <SectionHeader title="A Legacy of Responsibility" />
          <motion.div 
            className="legacy-content content-justified"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <p>With over 29 years of excellence, we continue to serve industries globally with pioneering solutions in:</p>
            <div className="legacy-card">
            <ul className="legacy-list content-justified">
              {legacyItems.map((item, index) => (
                <motion.li 
                  key={item}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  whileHover={{ x: 10 }}
                >
                    <FaChevronRight className="legacy-icon" />
                    <span>{item}</span>
                </motion.li>
              ))}
            </ul>
            </div>
            <div className="legacy-footer-container">
              <p className="legacy-footer">Our journey is one of quiet strength, ethical enterprise, and relentless innovation — growing by giving back.</p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="about-cta-section">
        <div className="about-cta-overlay"></div>
        <div className="container">
          <motion.div
            className="about-cta-container"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2>Ready to Explore Sustainable Solutions?</h2>
            <p>Discover how our green chemistry innovations can transform your business operations.</p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a href="/contact" className="about-cta-button">Contact Us Today</a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs; 